import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { db, functions } from "../firebase";

type FamilyRow = {
  id: string;
  familyName: string;
  familyCodeUpper: string;
  memberUids?: string[];
  studentKeys?: string[];
  settings?: {
    requireVolunteerApproval?: boolean;
    volunteerGoalHoursOverride?: number | null;
  };
};

type ParentRow = {
  id: string;
  parentKey: string;
  firstName: string;
  lastName: string;
  email: string;
  familyId?: string;
  isActive?: boolean;
};

type StudentRow = {
  id: string;
  studentKey: string;
  firstName: string;
  lastName: string;
  grade: string;
  familyId?: string;
  isActive?: boolean;
};

function fullName(r: { firstName?: string; lastName?: string }) {
  return `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim();
}

export function FamiliesPage() {
  const [rows, setRows] = useState<FamilyRow[]>([]);
  const [parents, setParents] = useState<ParentRow[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const [familyName, setFamilyName] = useState("");
  const [creating, setCreating] = useState(false);

  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [selectedParentId, setSelectedParentId] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");

  const [assigningParent, setAssigningParent] = useState(false);
  const [assigningStudent, setAssigningStudent] = useState(false);
  const [unlinkingParentId, setUnlinkingParentId] = useState<string | null>(null);
  const [unlinkingStudentId, setUnlinkingStudentId] = useState<string | null>(null);
  const [deletingFamily, setDeletingFamily] = useState(false);

  const [savingGoalOverride, setSavingGoalOverride] = useState(false);
  const [goalOverrideInput, setGoalOverrideInput] = useState("");

  useEffect(() => {
    const qRef = query(collection(db, "families"), orderBy("familyName", "asc"));
    return onSnapshot(
      qRef,
      (snap) => {
        const out: FamilyRow[] = snap.docs.map((d) => {
          const data: any = d.data();
          return {
            id: d.id,
            familyName: data.familyName ?? "",
            familyCodeUpper: data.familyCodeUpper ?? "",
            memberUids: data.memberUids ?? [],
            studentKeys: data.studentKeys ?? [],
            settings: data.settings ?? {},
          };
        });
        setRows(out);
        setErr(null);
      },
      (e) => setErr(e.message)
    );
  }, []);

  async function loadRosterLists() {
    try {
      const [pSnap, sSnap] = await Promise.all([
        getDocs(query(collection(db, "parents"), orderBy("lastName", "asc"))),
        getDocs(query(collection(db, "students"), orderBy("lastName", "asc"))),
      ]);

      setParents(pSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setStudents(sSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    } catch (e: any) {
      setErr(e?.message ?? "Failed to load parents/students.");
    }
  }

  useEffect(() => {
    loadRosterLists();
  }, []);

  const canCreate = useMemo(() => familyName.trim().length >= 2 && !creating, [familyName, creating]);

  const selectedFamily = useMemo(
    () => rows.find((r) => r.id === selectedFamilyId) || null,
    [rows, selectedFamilyId]
  );

  const selectedParent = useMemo(
    () => parents.find((p) => p.id === selectedParentId) || null,
    [parents, selectedParentId]
  );

  const selectedStudent = useMemo(
    () => students.find((s) => s.id === selectedStudentId) || null,
    [students, selectedStudentId]
  );

  const familyParents = useMemo(() => {
    if (!selectedFamily) return [];
    return parents.filter((p) => p.familyId === selectedFamily.id);
  }, [parents, selectedFamily]);

  const familyStudents = useMemo(() => {
    if (!selectedFamily) return [];
    return students.filter((s) => s.familyId === selectedFamily.id);
  }, [students, selectedFamily]);

  const parentCountByFamilyId = useMemo(() => {
    const counts = new Map<string, number>();
    parents.forEach((parent) => {
      if (!parent.familyId) return;
      counts.set(parent.familyId, (counts.get(parent.familyId) ?? 0) + 1);
    });
    return counts;
  }, [parents]);

  const studentCountByFamilyId = useMemo(() => {
    const counts = new Map<string, number>();
    students.forEach((student) => {
      if (!student.familyId) return;
      counts.set(student.familyId, (counts.get(student.familyId) ?? 0) + 1);
    });
    return counts;
  }, [students]);

  useEffect(() => {
    if (!selectedFamily) {
      setGoalOverrideInput("");
      return;
    }

    const current = selectedFamily.settings?.volunteerGoalHoursOverride;
    setGoalOverrideInput(
      current === null || current === undefined
        ? ""
        : String(current)
    );
  }, [selectedFamily?.id, selectedFamily?.settings?.volunteerGoalHoursOverride]);

  async function createFamily() {
    const name = familyName.trim();
    if (!name) return;

    setCreating(true);
    try {
      const fn = httpsCallable(functions, "createFamily");
      const res: any = await fn({ familyName: name });
      const familyId = res?.data?.familyId;
      const code = res?.data?.familyCode;
      alert(`Family created.\n\nName: ${name}\nFamily ID: ${familyId}\nFamily Code: ${code}`);
      setFamilyName("");
    } catch (e: any) {
      alert(e?.message ?? "Failed to create family.");
    } finally {
      setCreating(false);
    }
  }


  async function toggleRequireApproval(family: FamilyRow) {
    const current = family.settings?.requireVolunteerApproval === true;
    try {
      const fn = httpsCallable(functions, "upsertFamilyVolunteerSettings");
      await fn({
        familyId: family.id,
        requireVolunteerApproval: !current,
      });
    } catch (e: any) {
      alert(e?.message ?? "Failed to update setting.");
    }
  }

  async function saveGoalOverride() {
    if (!selectedFamily) {
      alert("Select a family first.");
      return;
    }

    setSavingGoalOverride(true);
    try {
      const fn = httpsCallable(functions, "upsertFamilyVolunteerSettings");
      await fn({
        familyId: selectedFamily.id,
        volunteerGoalHoursOverride: goalOverrideInput.trim() === "" ? null : Number(goalOverrideInput),
      });
      alert("Volunteer goal override saved.");
    } catch (e: any) {
      alert(e?.message ?? "Failed to save volunteer goal override.");
    } finally {
      setSavingGoalOverride(false);
    }
  }

  async function clearGoalOverride() {
    if (!selectedFamily) {
      alert("Select a family first.");
      return;
    }

    setSavingGoalOverride(true);
    try {
      const fn = httpsCallable(functions, "upsertFamilyVolunteerSettings");
      await fn({
        familyId: selectedFamily.id,
        volunteerGoalHoursOverride: null,
      });
      setGoalOverrideInput("");
      alert("Volunteer goal override cleared.");
    } catch (e: any) {
      alert(e?.message ?? "Failed to clear volunteer goal override.");
    } finally {
      setSavingGoalOverride(false);
    }
  }

  async function assignParent() {
    if (!selectedFamily || !selectedParent) {
      alert("Select both a family and a parent.");
      return;
    }
    if (!selectedParent.email) {
      alert("Selected parent is missing email.");
      return;
    }

    setAssigningParent(true);
    try {
      const fn = httpsCallable(functions, "assignParentToFamilyAdmin");
      await fn({
        familyId: selectedFamily.id,
        parentDocId: selectedParent.id,
        email: selectedParent.email,
      });
      await loadRosterLists();
      alert(`Assigned ${selectedParent.email} to ${selectedFamily.familyName}.`);
    } catch (e: any) {
      alert(e?.message ?? "Failed to assign parent.");
    } finally {
      setAssigningParent(false);
    }
  }

  async function assignStudent() {
    if (!selectedFamily || !selectedStudent) {
      alert("Select both a family and a student.");
      return;
    }

    setAssigningStudent(true);
    try {
      const fn = httpsCallable(functions, "assignStudentToFamilyAdmin");
      await fn({
        familyId: selectedFamily.id,
        studentDocId: selectedStudent.id,
      });
      await loadRosterLists();
      alert(`Assigned ${selectedStudent.studentKey} to ${selectedFamily.familyName}.`);
    } catch (e: any) {
      alert(e?.message ?? "Failed to assign student.");
    } finally {
      setAssigningStudent(false);
    }
  }

  async function unlinkStudent(student: StudentRow) {
    if (!confirm(`Unlink ${fullName(student)} from this family?`)) return;

    setUnlinkingStudentId(student.id);
    try {
      const fn = httpsCallable(functions, "unlinkStudentFromFamilyAdmin");
      await fn({ studentDocId: student.id });
      await loadRosterLists();
    } catch (e: any) {
      alert(e?.message ?? "Failed to unlink student.");
    } finally {
      setUnlinkingStudentId(null);
    }
  }

  async function unlinkParent(parent: ParentRow) {
    if (!confirm(`Unlink ${fullName(parent) || parent.email} from this family?\n\nThis will detach the parent from the household and remove family-derived student links. It will not delete the parent account.`)) return;

    setUnlinkingParentId(parent.id);
    try {
      const fn = httpsCallable(functions, "unlinkParentFromFamilyAdmin");
      await fn({ parentDocId: parent.id });
      await loadRosterLists();
    } catch (e: any) {
      alert(e?.message ?? "Failed to unlink parent.");
    } finally {
      setUnlinkingParentId(null);
    }
  }

  async function deleteSelectedFamily() {
    if (!selectedFamily) return;
    const name = selectedFamily.familyName || selectedFamily.id;
    if (!confirm(`Delete family "${name}"?\n\nThis only works for an empty family. Parents and students must be unlinked first.`)) return;

    setDeletingFamily(true);
    try {
      const fn = httpsCallable(functions, "deleteFamilyAdmin");
      await fn({ familyId: selectedFamily.id });
      setSelectedFamilyId("");
      await loadRosterLists();
      alert(`Deleted family "${name}".`);
    } catch (e: any) {
      alert(e?.message ?? "Failed to delete family.");
    } finally {
      setDeletingFamily(false);
    }
  }

  return (
    <div className="card">
      <h2>Families</h2>
      <p className="muted">
        Create families, assign parents and students, rotate enrollment codes, and manage family-level settings.
      </p>

      <hr className="hr" />

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input
          className="input"
          placeholder="New family name (e.g. Piel Family)"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          style={{ minWidth: 280 }}
        />
        <button className="btn" disabled={!canCreate} onClick={createFamily}>
          {creating ? "Creating…" : "Create family"}
        </button>
      </div>

      <hr className="hr" />

      <h3>Assign Parent / Student</h3>

      <div className="grid2" style={{ alignItems: "end" }}>
        <div>
          <label className="label">Family</label>
          <select className="input" value={selectedFamilyId} onChange={(e) => setSelectedFamilyId(e.target.value)}>
            <option value="">Select family…</option>
            {rows.map((f) => (
              <option key={f.id} value={f.id}>
                {f.familyName} ({f.id})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Parent</label>
          <select className="input" value={selectedParentId} onChange={(e) => setSelectedParentId(e.target.value)}>
            <option value="">Select parent…</option>
            {parents
              .filter((p) => p.isActive !== false)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {fullName(p)} — {p.email} ({p.parentKey}) {p.familyId ? `[${p.familyId}]` : ""}
                </option>
              ))}
          </select>
          <button className="btn secondary" disabled={assigningParent} onClick={assignParent} style={{ marginTop: 10 }}>
            {assigningParent ? "Assigning…" : "Assign Parent to Family"}
          </button>
        </div>

        <div>
          <label className="label">Student</label>
          <select className="input" value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
            <option value="">Select student…</option>
            {students
              .filter((s) => s.isActive !== false)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {fullName(s)} — {s.studentKey} (G{s.grade}) {s.familyId ? `[${s.familyId}]` : ""}
                </option>
              ))}
          </select>
          <button className="btn secondary" disabled={assigningStudent} onClick={assignStudent} style={{ marginTop: 10 }}>
            {assigningStudent ? "Assigning…" : "Assign Student to Family"}
          </button>
        </div>
      </div>

      {selectedFamily ? (
        <>
          <hr className="hr" />

          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>{selectedFamily.familyName}</h3>

            <div style={{ display: "grid", gap: 6 }}>
              <div><b>Family ID:</b> <code>{selectedFamily.id}</code></div>
              <div><b>Canonical Family ID:</b> <code>{selectedFamily.familyCodeUpper || selectedFamily.id || "(none)"}</code></div>
              <div><b>Parent Members:</b> {familyParents.length}</div>
              <div><b>Students:</b> {familyStudents.length}</div>
              <div>
                <b>Volunteer Goal Override:</b>{" "}
                {selectedFamily.settings?.volunteerGoalHoursOverride === null ||
                selectedFamily.settings?.volunteerGoalHoursOverride === undefined
                  ? "Using global goal"
                  : `${selectedFamily.settings?.volunteerGoalHoursOverride} hrs`}
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <button className="btn secondary" onClick={() => alert(`Family ID is system-generated, canonical across the app, and cannot be changed for ${selectedFamily.id}.`)}>
                Family ID is system-managed
              </button>

              <button className="btn secondary" disabled={deletingFamily} onClick={deleteSelectedFamily}>
                {deletingFamily ? "Deleting..." : "Delete Empty Family"}
              </button>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selectedFamily.settings?.requireVolunteerApproval === true}
                  onChange={() => toggleRequireApproval(selectedFamily)}
                />
                <span>Require volunteer approval</span>
              </label>
            </div>

            <div style={{ display: "grid", gap: 10, marginTop: 16, maxWidth: 420 }}>
              <label className="label">Volunteer Goal Override (hours)</label>
              <input
                className="input"
                type="number"
                min={0}
                step="0.25"
                placeholder="Leave blank to use global goal"
                value={goalOverrideInput}
                onChange={(e) => setGoalOverrideInput(e.target.value)}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn secondary" disabled={savingGoalOverride} onClick={saveGoalOverride}>
                  {savingGoalOverride ? "Saving…" : "Save Goal Override"}
                </button>
                <button className="btn secondary" disabled={savingGoalOverride} onClick={clearGoalOverride}>
                  Clear Override
                </button>
              </div>
            </div>
          </div>

          <div className="grid2" style={{ marginTop: 16 }}>
            <div className="card">
              <h3>Parents in Family</h3>
              {familyParents.length === 0 ? (
                <div className="muted">No parents assigned.</div>
              ) : (
                <div className="table">
                  <div className="thead" style={{ gridTemplateColumns: "1fr 220px 120px" }}>
                    <div>Name</div>
                    <div>Email</div>
                    <div></div>
                  </div>
                  {familyParents.map((p) => (
                    <div className="trow" key={p.id} style={{ gridTemplateColumns: "1fr 220px 120px" }}>
                      <div>{fullName(p)}</div>
                      <div>{p.email}</div>
                      <div style={{ textAlign: "right" }}>
                        <button
                          className="btn small secondary"
                          disabled={unlinkingParentId === p.id}
                          onClick={() => unlinkParent(p)}
                        >
                          {unlinkingParentId === p.id ? "Working..." : "Unlink"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card">
              <h3>Students in Family</h3>
              {familyStudents.length === 0 ? (
                <div className="muted">No students assigned.</div>
              ) : (
                <div className="table">
                  <div className="thead" style={{ gridTemplateColumns: "1fr 90px 120px" }}>
                    <div>Student</div>
                    <div>Grade</div>
                    <div></div>
                  </div>
                  {familyStudents.map((s) => (
                    <div className="trow" key={s.id} style={{ gridTemplateColumns: "1fr 90px 120px" }}>
                      <div>{fullName(s)} ({s.studentKey})</div>
                      <div>{s.grade}</div>
                      <div style={{ textAlign: "right" }}>
                        <button
                          className="btn small secondary"
                          disabled={unlinkingStudentId === s.id}
                          onClick={() => unlinkStudent(s)}
                        >
                          {unlinkingStudentId === s.id ? "Working…" : "Unlink"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {err ? (
        <p style={{ color: "tomato", marginTop: 12 }}>
          Error: {err}
        </p>
      ) : null}

      <hr className="hr" />

      <table className="table family-summary-table">
        <colgroup>
          <col className="family-summary-name" />
          <col className="family-summary-code" />
          <col className="family-summary-count" />
          <col className="family-summary-count" />
          <col className="family-summary-approval" />
          <col className="family-summary-goal" />
          <col className="family-summary-action" />
        </colgroup>
        <thead>
          <tr>
            <th>Family</th>
            <th>Code</th>
            <th>Parents</th>
            <th>Students</th>
            <th>Volunteer approval</th>
            <th>Goal override</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const require = r.settings?.requireVolunteerApproval === true;
            const goalOverride = r.settings?.volunteerGoalHoursOverride;
            const parentCount = parentCountByFamilyId.get(r.id) ?? 0;
            const studentCount = studentCountByFamilyId.get(r.id) ?? 0;
            return (
              <tr key={r.id}>
                <td>
                  <div><b>{r.familyName}</b></div>
                  <div className="muted" style={{ fontSize: 12 }}>{r.id}</div>
                </td>
                <td><code>{r.familyCodeUpper || "(none)"}</code></td>
                <td className="family-summary-number">{parentCount}</td>
                <td className="family-summary-number">{studentCount}</td>
                <td>{require ? "Required" : "Trusted by default"}</td>
                <td>
                  {goalOverride === null || goalOverride === undefined
                    ? "Global"
                    : `${goalOverride} hrs`}
                </td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn secondary" onClick={() => setSelectedFamilyId(r.id)}>
                    Manage
                  </button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="muted">No families yet.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
